import { ref } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { HttpDelete, HttpGet, HttpPost } from '@/services/Service'

export const AuthPage = {
  LOGIN: 100,
  FORGET_PASSWORD: 200,
  TWO_FACTOR_AUTH: 300,
  TWO_FACTOR_AUTH_INTRO: 400,
  TWO_FACTOR_AUTH_INFO: 500,
  TWO_FACTOR_AUTH_SETUP: 600,
} as const

export type AuthPageType = typeof AuthPage[keyof typeof AuthPage];

export type MaintenanceNotification = {
  message: string;
  type: 'information' | 'warning' | 'error';
  uuid: string;
  visible: boolean;
}

export type NavigationStep = {
  id: AuthPageType;
  title: string;
  icon_class: string;
}

export type AuthRequest = CustomAxiosResponse & {
  data: {
    two_factor: boolean;
  }
}

export const useAuthStore = defineStore('auth', () => {
  const page = ref<AuthPageType>(AuthPage.LOGIN)
  const email = ref<string>('')
  const password = ref<string>('')
  const authCode = ref<string>('')
  const qrCode = ref<string>('')
  const rememberMe = ref<boolean>(false)
  const authCodeInvalid = ref<boolean>(false)
  const twoFactorConfirmed = ref<boolean>(false)
  const userTwoFactorEnabled = ref<boolean>(false)
  const userCompletedTwoFactorAuth = ref<boolean>(false)
  const showPassword = ref<boolean>(false)
  const hasValidationError = ref<boolean>(false)
  const requestMessage = ref<string>('')
  const maintenanceNotification = ref<MaintenanceNotification | null>(null)

  const navigationSteps = ref<NavigationStep[]>([
    {
      id: AuthPage.TWO_FACTOR_AUTH_INTRO,
      title: 'Verificatie in twee stappen',
      icon_class: 'fas fa-info'
    },
    {
      id: AuthPage.TWO_FACTOR_AUTH_INFO,
      title: 'App installeren',
      icon_class: 'far fa-mobile'
    },
    {
      id: AuthPage.TWO_FACTOR_AUTH_SETUP,
      title: 'Verificatiecode invoeren',
      icon_class: 'fal fa-right-to-bracket'
    }
  ])

  const togglePassword = () => {
    return showPassword.value = !showPassword.value
  }

  const login = async () => {
    return await HttpPost(`/login`, {
      email: email.value.trim(),
      password: password.value,
      web: true,
    })
  }

  const forgotPassword = async () => {
    return await HttpPost(`/api/password/email`, {
      email: email.value.trim()
    })
  }

  // Submit the two factor challenge to the server.
  const twoFactorChallenge = async () => {
    return await HttpPost(`/two-factor-challenge`, {
      code: authCode.value.replace(/\s/g, ''), // replaces all spaces with an empty string
      remember: rememberMe.value,
    })
  }

  // Submit the two factor challenge to the server.
  const confirmTwoFactorChallenge = async () => {
    return await HttpPost(`/user/confirmed-two-factor-authentication`, {
      code: authCode.value.replace(/\s/g, ''), // replaces all spaces with an empty string
    })
  }

  // Check if the user/tenant has two factor authentication enabled.
  const checkTwoFactorAuthEnabled = async () => {
    const response: any = await HttpGet('/api/v1/user/2FA/check')

    userTwoFactorEnabled.value = !!response.data.user_enabled
    twoFactorConfirmed.value = !!response.data.two_factor_confirmed

    if (twoFactorConfirmed.value) {
      userCompletedTwoFactorAuth.value = true
    }
  }

  const enableTwoFactorAuth = async () => {
    return await HttpPost('/user/two-factor-authentication')
  }

  const disableTwoFactorAuth = async () => {
    return await HttpDelete('/user/two-factor-authentication')
  }

  // Fetch the two factor QR-code
  const fetchQrCode = async () => {
    const response = await HttpGet('/user/two-factor-qr-code')

    qrCode.value = response.data.svg ?? ''
  }

  // Reset the two factor auth
  const resetTwoFactorAuth = async () => {
    await disableTwoFactorAuth()

    return await HttpPost('/api/v1/user/2FA/reset')
  }

  return {
    // state
    email,
    password,
    authCode,
    qrCode,
    page,
    authCodeInvalid,
    showPassword,
    requestMessage,
    hasValidationError,
    twoFactorConfirmed,
    userTwoFactorEnabled,
    userCompletedTwoFactorAuth,
    maintenanceNotification,
    rememberMe,

    // navigation
    navigationSteps,

    // actions
    togglePassword,
    login,
    forgotPassword,
    twoFactorChallenge,
    confirmTwoFactorChallenge,
    checkTwoFactorAuthEnabled,
    enableTwoFactorAuth,
    fetchQrCode,
    resetTwoFactorAuth,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
